import { h, defineComponent, ref, watch } from 'vue';
// @ts-ignore
import { t } from '@/ui/i18n/i18n';
import * as qs from '@/ui/queryState';
import { box, indexView, level, loader, pageTitle, pagination } from '@/ui/components';
import { list as getChecklistList } from '@/services/api/resources/checklist';
import { emptyList } from '@/services/api';
import { ChecklistList, ChecklistRecord, Status } from '@/entities/checklist';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { ResourceTable } from '@/ui/components/table/ResourceTable';
import { hasFeature, hasPermission, Permissions, user } from '@/ui/auth/auth';
import { CLICK_EVENTS, FilterTypes, trackClick, trackFilter } from '@/services/activityTracker/activityTrackerHelper';
import { searchFilter } from '@/ui/components/filters/filters-horizontal';
import { SortDirection } from '@/ui/types';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { useChecklistCheck } from '@/ui/pages/checklist/useChecklistCheck';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { pushRoute } from '@/ui/helpers';
import { inspectionFromTemplateEmpty } from '@/ui/pages/inspection/InspectionFromTemplateEmpty';
import { filterFormHorizontal } from '@/ui/components/Columns';

export const InspectionFromTemplatePage = defineComponent({
  name: 'InspectionFromTemplatePage',
  setup() {
    if (!hasFeature('inspections')) {
      return renderResourceTeaserPage(t('common.inspections'));
    }

    if (!hasPermission(Permissions.CONDUCT_OPEN_INSPECTIONS)) {
      return renderForbiddenPage;
    }

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir } = useSorting('createdAt', SortDirection.Descending);
    const { checkResult, performCheck } = useChecklistCheck();

    const search = qs.string('q', '');
    watch(search, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        trackFilter(FilterTypes.search, { page: 'Templates Index' });
      }
    });

    // get list task
    const list = ref<ChecklistList>(emptyList());
    const getList = useTask(
      () => getChecklistList({
        page: page.value,
        perPage: perPage.value,
        search: search.value,
        category: [],
        status: Status.Active,
        conductibleBy: [user.value.id],
        sort: sort.value,
        dir: dir.value,
      }),
      listResponse => {
        list.value = listResponse;
      },
    );
    watch([page, perPage, sort, dir], getList.run);

    return function render() {
      return loader('div', performCheck, [
        level([
          pageTitle(t('inspections.start-from-tpl')),
        ]),
        checkResult.value
          ? indexView([
            filterFormHorizontal([searchFilter(search)]),
          ],
          [
            box([
              loader('div', getList, [
                h(ResourceTable, {
                  class: 'selectable-table',
                  props: {
                    titleColumnLabel: t('inspections.title-of-template'),
                    list: list.value.data,
                    sort: sort.value,
                    dir: dir.value,
                    clickableTitle: false,
                    sortableTitle: false,
                  },
                  scopedSlots: {
                    title: ({ row: checklist }: { row: ChecklistRecord }) => {
                      return checklist.data.title || t('checklist.no-title');
                    },
                  },
                  on: {
                    select: (row: { id: number }) => {
                      trackClick(CLICK_EVENTS.CREATE_INSPECTION);
                      pushRoute({
                        name: 'inspectionCreate',
                        params: { id: row.id.toString() },
                      });
                    },
                    sort: (field: string, direction: SortDirection) => qs.updateMultiple(() => {
                      resetPagination();
                      sort.value = field;
                      dir.value = direction;
                    }),
                  },
                }),
                pagination({
                  current: page.value,
                  total: list.value.meta.total,
                  perPage: perPage.value,
                }),
                //needs to re-mount to display the title
              ], false),
            ]),
          ])
          : box([inspectionFromTemplateEmpty()]),
      ], false);
    };
  },
});
